import { useApi } from "@/hooks";
import ContainerWrapper from "@Components/generic/container-wrapper";
import { AppContext, Events } from "@Context/common/general/context";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import useI18n from "@i18n";
import { Card, CardContent, CircularProgress, Container, Stack, Typography, useTheme } from "@mui/material";
import { Api } from "@Shared/schemas";
import { BybcatConfigType, BybcatResultStatus, BybcatResultType, BybcatUserType } from "@Shared/schemas/bybcat/bybcatSchemas";
import { useContext, useEffect, useState } from "react";
import { useUnmount } from "react-use";

const BybCatLauncher = () => {
  const theme = useTheme();
  const { state } = useContext(AppContext);
  const { i18n } = useI18n();
  const [userMap, setUserMap] = useState<Record<number, BybcatUserType>>({});
  const [configMap, setConfigMap] = useState<Record<number, BybcatConfigType>>({});
  const [loading, setLoading] = useState(true);

  const {
    call: getUsers,
    data: users = [],
    loading: getUsersLoading,
  } = useApi<typeof Api.Bybcat.GetUsers>({
    api: Api.Bybcat.GetUsers,
    request: { account_id: state.account.id! },
  });

  const {
    call: getResults,
    data: results = [],
    loading: getResultsLoading,
  } = useApi<typeof Api.Bybcat.GetResults>({
    api: Api.Bybcat.GetResults,
    request: { account_id: state.account.id!, status: BybcatResultStatus.SCHEDULED },
  });

  const {
    call: getConfigs,
    data: configs = [],
    loading: getConfigsLoading,
  } = useApi<typeof Api.Bybcat.GetConfigs>({
    api: Api.Bybcat.GetConfigs,
    request: { account_id: state.account.id! },
  });

  useEffect(() => {
    Events.app.showHeaderButton.emit({ type: VRHeaderButtonType.BACK, title: i18n("bybcat.page.title") });
    getUsers();
    getResults();
    getConfigs();
    return () => Events.app.hideHeaderButton.emit();
  }, []);

  useEffect(() => {
    setLoading(getUsersLoading || getResultsLoading || getConfigsLoading);
  }, [getUsersLoading, getResultsLoading, getConfigsLoading]);

  useEffect(() => {
    if (users && users.length > 0) {
      setUserMap(
        users.reduce(
          (acc, user) => {
            acc[user.id] = user;
            return acc;
          },
          {} as Record<number, BybcatUserType>
        )
      );
    }
  }, [users]);

  useEffect(() => {
    if (configs && configs.length > 0) {
      setConfigMap(
        configs.reduce(
          (acc, config) => {
            acc[config.id] = config;
            return acc;
          },
          {} as Record<number, BybcatConfigType>
        )
      );
    }
  }, [configs]);

  useUnmount(() => {
    Events.app.hideHeaderButton.emit();
  });

  const navigate = (result: BybcatResultType) => {
    Events.app.navigate.emit({ path: `/coach/bybcat/result/${result.id}` });
  };

  return (
    <ContainerWrapper id="BYBCAT-LAUNCHER">
      <Container maxWidth="sm" sx={{ padding: theme.spacing(0, 0, 6) }}>
        <Stack spacing={6}>
          <Stack spacing={4}>
            <Typography variant="text4xl700" color="dsBase.v200">
              {i18n("bybcat.page.title")}
            </Typography>
            {loading ? (
              <Stack sx={{ pt: 32 }} justifyContent={"center"} alignItems={"center"}>
                <CircularProgress />
              </Stack>
            ) : (
              <Stack spacing={4}>
                <Typography variant="text2xl400" color="dsBase.v200">
                  {i18n("bybcat.page.welcome")}
                </Typography>
                {results && results.length > 0 ? (
                  <Typography variant="text2xl400" color="dsBase.v200">
                    {i18n("bybcat.page.instructions_1")}
                  </Typography>
                ) : (
                  <Typography variant="text2xl400" color="dsBase.v200" sx={{ lineHeight: "2.5", whiteSpace: "pre-line" }}>
                    {i18n("bybcat.page.instructions_3")}
                  </Typography>
                )}
                <Stack spacing={2}>
                  {results?.map(result => (
                    <Card sx={theme => ({ p: theme.spacing(2.25, 2.5) })} onClick={() => navigate(result)}>
                      <CardContent>
                        <Stack spacing={1}>
                          <Typography variant="text3xl600" color="dsBase.v200">
                            {userMap[result.bybcat_user_id]?.name}
                          </Typography>
                          <Typography variant="text3xl500" color="dsNeutral.v300">
                            {configMap[result.bybcat_config_id]?.name}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Container>
    </ContainerWrapper>
  );
};

export default BybCatLauncher;
